<template>
    <v-card
        :class="cardClass"
        flat
        :color="cardColor"
    >
        <v-card-title class="pa-1" v-if="showCloseButton">
            <v-spacer></v-spacer>
            <v-btn
                class="ma-0"
                @click.stop="$emit('close')"
                flat
                icon
            >
                <v-icon>close</v-icon>
            </v-btn>
        </v-card-title>

        <reset-password-request-form
            v-if="showResetPasswordRequestForm"
            @hide="showResetPasswordRequestForm = false"
            :no-title="noTitle"
        />

        <change-password-form
            v-else-if="showChangePasswordForm"
            @hide="showChangePasswordForm = false"
            :token="resetPasswordToken"
            :no-title="noTitle"
        />

        <v-form
            v-else
            ref="loginForm"
            lazy-validation
            @submit.prevent="authenticateCustomer"
        >
            <v-card-text class="py-0">
                <third-party-login-buttons
                    @error="handleAuthError"
                    @authenticated="$emit('authenticated')"
                    :third-party-login-buttons-icons-only="thirdPartyLoginButtonsIconsOnly"
                />
            </v-card-text>

            <v-card-text class="py-0">
                <p v-if="!noTitle" :class="loginLabelClassName">
                    {{ 'Log in' | translation }}
                </p>

                <v-layout row wrap align-center class="my-0">
                    <v-flex gorw class="pr-0">
                        <div class="title-line"></div>
                    </v-flex>
                    <v-flex shrink class="py-0">
                        <p class="text-xs-center ma-0" :class="loginLabelSubtitleClassName">
                            {{ 'With your account credentials you can easily log in here.' | translation }}
                        </p>
                    </v-flex>
                    <v-flex gorw class="pl-0">
                        <div class="title-line"></div>
                    </v-flex>
                </v-layout>
            </v-card-text>

            <v-card-text v-if="!thirdPartyLoginButtonsOnly" class="pt-0">
                <p class="body-2 ma-0" v-if="inputLabelOutside">{{ 'E-Mail Adresse' | translation }}</p>
                <v-text-field
                    :solo="inputSolo"
                    :label="inputLabelOutside ? '' : $language.translate('E-Mail Adresse')"
                    v-model="login"
                    :rules="[rules.required, rules.email]"
                    required
                    :error-messages="errors['login']"
                    @change="resetErrors('login')"
                />

                <p class="body-2 ma-0" v-if="inputLabelOutside">{{ 'Passwort' | translation }}</p>
                <v-text-field
                    :solo="inputSolo"
                    :label="inputLabelOutside ? '' : $language.translate('Passwort')"
                    v-model="password"
                    autocomplete="new-password"
                    :append-icon="showPassword ? 'visibility' : 'visibility_off'"
                    :type="showPassword ? 'text' : 'password'"
                    :error-messages="errors['password']"
                    :rules="[rules.required, rules.length]"
                    @click:append="showPassword = !showPassword"
                    @change="resetErrors('password')"
                    required
                />

                <div class="text-xs-right">
                    <a href="#" @click.prevent="showResetPasswordRequestForm = true">
                        {{ 'Forgot password?' | translation }}
                    </a>
                </div>

                <v-btn
                    block
                    color="primary"
                    large
                    type="submit"
                    :round="isSubmitButtonRound"
                    :depressed="loginBtnDepressed"
                >
                    {{ 'Log In' | translation }}
                </v-btn>
            </v-card-text>
        </v-form>
    </v-card>
</template>

<style lang="stylus">
@import '~vuetify/src/stylus/settings/_colors.styl';

.title-line {
    width: 100%;
    height: 1px;
    background: $grey.lighten-3;
}
</style>

<script>
    import Vue from 'vue';
    import {mapActions} from 'vuex';
    import ResetPasswordRequestForm from './ResetPasswordRequestForm';
    import ChangePasswordForm from './ChangePasswordForm';
    import ThirdPartyLoginButtons from '../3rdPartyLogin/ThirdPartyLoginButtons';

    const moduleName = 'Login';

    export default {
        name: moduleName,
        components: {
            ResetPasswordRequestForm,
            ChangePasswordForm,
            ThirdPartyLoginButtons,
        },
        props: {
            noTitle: Boolean,
            inputSolo: Boolean,
            inputLabelOutside: Boolean,
            isSubmitButtonRound: Boolean,
            cardClass: {
                type: String,
                default: 'text-xs-center'
            },
            cardColor: {
                type: String,
                default: 'text-xs-center'
            },
            loginBtnDepressed: {
                type: Boolean,
                default: true,
            },
            loginLabelClassName: {
                default: 'headline font-weight-bold mb-0',
            },
            loginLabelSubtitleClassName: {
                default: '',
            },
            thirdPartyLoginButtonsIconsOnly: Boolean,
            thirdPartyLoginButtonsOnly: Boolean,
        },
        data() {
            return {
                login: null,
                password: null,
                showPassword: false,
                errors: {},
                rules: {
                    required: value => !!value || 'Dieses Feld wird benötigt und darf nicht leer sein.',
                    length: value => (value ? (value.length >= 7 || 'Das Passwort muss mindestens 7 Zeichen haben.') : ''),
                    email: value => /.+@.+/.test(value) || 'Bitte geben sie eine gültige Email-Adresse ein.'
                },
                showResetPasswordRequestForm: false,
                showChangePasswordForm: false,
                resetPasswordToken: null,
                showCloseButton: false,
            };
        },
        created() {
            if (this.$route.query['reset-password-token']) {
                this.resetPasswordToken = this.$route.query['reset-password-token'];
                this.showChangePasswordForm = true;
                this.showCloseButton = true;
                this.$emit('persist-form');
                this.$router.replace({
                    ...this.$route,
                    query: {
                        ...this.$route.query,
                        'reset-password-token': undefined,
                    },
                });
            }
        },
        methods: {
            ...mapActions(moduleName, ['authenticate']),
            emitCloseEvent() {
                this.showCloseButton = false;
                this.$emit('close');
            },
            resetErrors(field) {
                if (field && this.errors.hasOwnProperty(field)) {
                    delete this.errors[field];
                }
            },
            authenticateCustomer() {
                if (this.$refs.loginForm.validate()) {
                    this.authenticate({
                        login: this.login,
                        password: this.password,
                    })
                        .then(() => {
                            this.$emit('authenticated');
                            if (this.$router.currentRoute.name === 'confirm-registration') {
                                this.$router.push('/');
                            }
                        })
                        .catch(error => {
                            this.handleAuthError(error);
                        });
                }
            },
            handleAuthError(error) {
                if (error.graphQLErrors.length > 0) {
                    error.graphQLErrors.some(errorData => {
                        Vue.set(this.errors, 'login', this.$language.translate(errorData.message));
                        return true;
                    });
                } else {
                    Vue.set(this.errors, 'login', this.$language.translate(error.message));
                }
            }
        },
    };
</script>